import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PricingReviewPanel from "./PricingReview/PricingReviewPanel";
import ListingReview from "./ListingReview";
import styles from "../../styles/opsConsole.module.css";
import ui from "./reviewChrome.module.css";
import {
  Tile,
  Kpi,
  IcoInbox,
  IcoWarn,
  IcoShield,
  IcoClock,
} from "../../styles/opsBits";

/**
 * PRODUCT APPROVALS
 * =================
 * A review queue, and a review screen that leads with the decision.
 *
 * What this replaces:
 *   • A card grid where each card showed a logo, a name, and a truncated blurb
 *     — not the vendor, not the date, not what had changed, not whether the
 *     price had moved. Everything you needed to triage was missing.
 *   • A status dropdown wired to nothing: `filteredAppsData` filtered on search
 *     and company only, so picking a status did literally nothing.
 *   • Only ever `status = "reviewing"`. An approved or rejected submission
 *     could not be looked at again.
 *   • Clicking a card swapped page state with no URL change, so refresh or back
 *     lost your place and a review could not be linked to a colleague.
 *   • Reject sent `{skuId, vendorId}` and no reason, anywhere.
 *
 * The research on comparable marketplaces (AWS, Microsoft Partner Center,
 * Atlassian, Shopify) all converge on the same two ideas, and both are here:
 * review the DELTA rather than the document, and put what the reviewer must
 * act on at the top of the screen rather than buried in it.
 */

const API = process.env.REACT_APP_SERVER_URL;

const json = (path, opts) =>
  fetch(`${API}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    ...opts,
  }).then((r) => r.json());

const post = (path, body) =>
  json(path, { method: "POST", body: JSON.stringify(body) });

const STATUS = {
  reviewing: { label: "Open", cls: "warn" },
  changes_requested: { label: "Changes requested", cls: "info" },
  approved: { label: "Approved", cls: "ok" },
  rejected: { label: "Rejected", cls: "danger" },
  cancelled: { label: "Withdrawn", cls: "muted" },
};

const CHANGE_TYPE = {
  new_listing: "New listing",
  price_update: "Price change",
  content_update: "Content change",
  // A vendor on a shared product cannot add a plan, add a billing period or
  // rename one — the shelf belongs to every vendor on it — so they ask, and
  // the ask lands in THIS queue rather than a second inbox nobody visits.
  // The decision is a different one, so the row opens a different screen.
  catalog_request: "Catalogue request",
};

/** "2 days ago" — age in a queue is the thing you triage on. */
const ago = (d) => {
  if (!d) return "—";
  const ms = Date.now() - new Date(d).getTime();
  const days = Math.floor(ms / 86400000);
  if (days > 0) return `${days} day${days === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(ms / 3600000);
  if (hrs > 0) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  return "just now";
};

const Badge = ({ kind, children, dot = true }) => (
  <span className={`${styles.badge} ${ui.badge} ${styles[kind] || ""}`}>
    {dot && <i className={styles.dot} />}
    {children}
  </span>
);

/**
 * Where you are, and the way back out.
 *
 * The previous control was a flat grey pill wrapping a MUI IconButton, which
 * read as a disabled button sitting above the title rather than as navigation
 * — and it competed for attention with Approve. A breadcrumb is what every
 * console this screen is measured against (Salesforce, Stripe, Cloud Console)
 * uses for a record opened out of a list: the parent list is the only clickable
 * part, the current record is plain text, and the whole thing costs one line.
 */
const Crumbs = ({ onBack, here }) => (
  <nav className={ui.crumbs} aria-label="Breadcrumb">
    <button type="button" className={ui.crumbBack} onClick={onBack}>
      <svg viewBox="0 0 24 24" className={ui.crumbChevron} aria-hidden="true">
        <path d="M15 18 9 12l6-6" />
      </svg>
      All approvals
    </button>
    <span className={ui.crumbSep} aria-hidden="true">/</span>
    <span className={ui.crumbHere} aria-current="page">{here}</span>
  </nav>
);

/* ══════════════════════════════ QUEUE ═════════════════════════════════ */

function Queue() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("reviewing");

  useEffect(() => {
    json("/admin/approvals")
      .then((r) => setRows(r?.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach((r) => (c[r.status] = (c[r.status] || 0) + 1));
    return c;
  }, [rows]);

  const overCap = useMemo(() => rows.filter((r) => r.over_cap).length, [rows]);

  const unverified = useMemo(
    () =>
      new Set(
        rows
          .filter(
            (r) =>
              r.status === "reviewing" && r.vendor_verification !== "verified",
          )
          .map((r) => r.vendor_id),
      ).size,
    [rows],
  );

  /** Age of the oldest thing still waiting — the number that should hurt. */
  const oldest = useMemo(() => {
    const open = rows.filter((r) => r.status === "reviewing");
    if (!open.length) return 0;
    return Math.max(
      ...open.map((r) =>
        Math.floor((Date.now() - new Date(r.submitted_at).getTime()) / 86400000),
      ),
    );
  }, [rows]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== "all" && r.status !== tab) return false;
      if (!needle) return true;
      return [r.sku_name, r.vendor_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle));
    });
  }, [rows, q, tab]);

  if (loading) {
    return (
      <div className={styles.centre}>
        <CircularProgress size={28} />
      </div>
    );
  }

  return (
    <div className={`${styles.root} ${ui.root}`}>
      <div className={`${styles.head} ${ui.head} ${ui.headQueue}`}>
        <div>
          <h1 className={`${styles.h1} ${ui.h1}`}>Product approvals</h1>
          <p className={`${styles.sub} ${ui.subProse}`}>
            What vendors have submitted. Approving publishes it to buyers —
            Easexpense is merchant of record, so a price you approve is a price
            someone is charged.
          </p>
        </div>
        <TextField
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search product or vendor"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {/* Shape of the queue before any row is read.
          A KPI only earns its colour when it has something to say: a count of
          zero is not a problem, so it stays neutral. Painting every tile red
          and amber on a clean queue is what turns severity colour into
          wallpaper. */}
      <div className={`${styles.kpis} ${ui.kpis}`}>
        <Kpi
          value={counts.reviewing || 0}
          label="Waiting for review"
          tone="toneInfo"
          icon={IcoInbox}
        />
        <Kpi
          value={overCap}
          label="Over the ±25% price cap"
          tone={overCap > 0 ? "toneDanger" : ""}
          icon={IcoWarn}
        />
        <Kpi
          value={unverified}
          label="Unverified vendors in queue"
          tone={unverified > 0 ? "toneWarn" : ""}
          icon={IcoShield}
        />
        <Kpi
          value={oldest ? `${oldest}d` : "—"}
          label="Longest wait"
          tone={oldest > 7 ? "toneDanger" : oldest ? "toneOk" : ""}
          icon={IcoClock}
        />
      </div>

      {/* Filters that filter. The old status dropdown was decorative. */}
      <div className={`${styles.filters} ${ui.filters}`}>
        {[
          ["reviewing", "Open"],
          ["changes_requested", "Changes requested"],
          ["approved", "Approved"],
          ["rejected", "Rejected"],
          ["all", "All"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`${styles.chip} ${ui.chip} ${
              tab === key ? `${styles.chipOn} ${ui.chipOn}` : ""
            }`}
          >
            {label}
            <span className={`${styles.chipCount} ${ui.chipCount}`}>
              {counts[key] || 0}
            </span>
          </button>
        ))}
      </div>

      <div className={`${styles.card} ${ui.card}`}>
        <table className={`${styles.table} ${ui.table}`}>
          <thead>
            <tr>
              <th>Product</th>
              <th>Vendor</th>
              <th>What changed</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  Nothing here.
                </td>
              </tr>
            )}
            {shown.map((r) => {
              const st = STATUS[r.status] || { label: r.status, cls: "muted" };
              return (
                <tr
                  key={
                    r.approval_id
                      ? `a${r.approval_id}`
                      : `p${r.proposal_id}`
                  }
                  className={`${styles.row} ${ui.row} ${
                    r.over_cap
                      ? styles.rowDanger
                      : r.vendor_verification !== "verified" &&
                          r.status === "reviewing"
                        ? styles.rowWarn
                        : r.status === "approved"
                          ? styles.rowOk
                          : ""
                  }`}
                  onClick={() =>
                    navigate(
                      r.change_type === "catalog_request"
                        ? `/dashboard/catalog_proposals?focus=${r.proposal_id}`
                        : `/dashboard/productapproval/${r.approval_id}`,
                    )
                  }
                >
                  <td>
                    <span className={styles.idCell}>
                      <Tile name={r.sku_name} src={r.logo} />
                      <span>
                        <b className={styles.primary}>{r.sku_name}</b>
                        <span className={styles.secondary}>
                          {r.is_owner ? "Owner" : "Reseller pricing"}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className={styles.primary}>{r.vendor_name}</span>
                    {/* The single most useful thing to know before opening it. */}
                    {r.vendor_verification !== "verified" && (
                      <Badge kind="warn">{r.vendor_verification}</Badge>
                    )}
                  </td>
                  <td>
                    {CHANGE_TYPE[r.change_type] || "—"}
                    {r.over_cap && (
                      <Badge kind="danger">
                        over cap · {r.cap_findings}
                      </Badge>
                    )}
                    {/* A catalogue request is one sentence long, so say it
                        here — there is nothing to triage without it. */}
                    {r.proposal_summary && (
                      <span className={styles.secondary}>
                        {r.proposal_summary}
                      </span>
                    )}
                  </td>
                  <td>
                    <Badge kind={st.cls}>{st.label}</Badge>
                  </td>
                  <td className={styles.nowrap}>{ago(r.submitted_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════════ REVIEW ═════════════════════════════════ */

/** Count what moved in one section of the diff, so the reviewer sees shape first. */
const delta = (log, key) => {
  const before = log?.[key]?.before?.length ?? 0;
  const after = log?.[key]?.after?.length ?? 0;
  if (!log?.[key]) return null;
  return { before, after, net: after - before };
};

function Review({ approvalId }) {
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("summary");
  // Bumped by the header "Approve…" button; the pricing panel opens its
  // confirm dialog (with the existing-customer options) when it changes.
  const [approveRequest, setApproveRequest] = useState(0);
  const [asking, setAsking] = useState(null); // "changes" | "reject"
  const [note, setNote] = useState("");
  const [deep, setDeep] = useState(null); // props for the legacy pricing view

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      const list = await json("/admin/approvals");
      const r = (list?.data || []).find(
        (x) => Number(x.approval_id) === Number(approvalId),
      );
      if (dead) return;
      setRow(r || null);
      if (r) {
        const d = await post("/admin/get_vendor_product", {
          approval_id: r.approval_id,
        });
        if (!dead) setDetail(d);
      }
      if (!dead) setLoading(false);
    })();
    return () => {
      dead = true;
    };
  }, [approvalId]);

  const log = detail?.changeJson || {};
  const cap = log?.price_cap || {};
  const findings = cap.findings || [];

  const decide = async (kind) => {
    if ((kind === "changes" || kind === "reject") && note.trim().length < 5) {
      setAsking(kind);
      return;
    }
    setBusy(true);
    let res;
    if (kind === "changes") {
      res = await post("/admin/request_changes", {
        approval_id: row.approval_id,
        note: note.trim(),
      });
    } else if (kind === "reject") {
      res = await post("/admin/reject_product", {
        skuId: row.sku_id,
        vendorId: row.vendor_id,
        reason: note.trim(),
      });
    }
    setBusy(false);
    if (res?.success !== false) {
      navigate("/dashboard/productapproval");
    } else {
      // eslint-disable-next-line no-alert
      alert(res?.msg || "That did not work");
    }
  };

  if (loading) {
    return (
      <div className={styles.centre}>
        <CircularProgress size={28} />
      </div>
    );
  }
  if (!row)
    return (
      <div className={`${styles.root} ${ui.root}`}>Submission not found.</div>
    );

  // The legacy pricing view wants the pending-products row shape.
  const legacyRow = {
    id: row.sku_id,
    approval_id: row.approval_id,
    vendor_id: row.vendor_id,
    name: row.sku_name,
    vendor_status: row.is_owner ? "owner" : "multiple",
    change_type: row.change_type,
  };

  /**
   * Pinned above everything. Copied deliberately from Stripe's connected-account
   * page: the reviewer should never have to read the record to find the ask.
   */
  const attention = [];
  if (row.vendor_verification !== "verified") {
    attention.push({
      severity: "block",
      text: `${row.vendor_name} is ${row.vendor_verification}. Approving publishes a listing from a supplier nobody has checked.`,
      action: { label: "Open vendor", to: `/dashboard/vendors/${row.vendor_id}` },
    });
  }
  if (cap.over_cap) {
    attention.push({
      severity: "block",
      text: `${findings.length} price${findings.length === 1 ? "" : "s"} exceed the ±25%/year cap. See below.`,
    });
  }
  // Listing content is live, not staged — but a listing missing the basics a
  // buyer needs should not be waved through on the strength of its prices.
  const product = detail?.product;
  if (product) {
    const meta =
      (typeof product.metaData === "string"
        ? (() => {
            try {
              return JSON.parse(product.metaData);
            } catch (e) {
              return null;
            }
          })()
        : product.metaData) || {};
    const listing = { label: "Open listing", tab: "listing" };
    if (!String(product.logo || "").trim()) {
      attention.push({ severity: "info", text: "The listing has no logo.", action: listing });
    }
    if (!String(meta?.legal?.terms_url || "").trim()) {
      attention.push({ severity: "block", text: "No terms of service URL — buyers have nothing to agree to.", action: listing });
    }
    if (!String(meta?.support?.email || "").trim()) {
      attention.push({ severity: "info", text: "No support email on the listing.", action: listing });
    }
    if (!String(meta?.fulfilment?.method || "").trim()) {
      attention.push({ severity: "info", text: "No fulfilment method — nobody has said how an order gets set up.", action: listing });
    }
  }
  if (!row.is_owner) {
    attention.push({
      severity: "info",
      text: "This vendor resells a product they do not own — only their own prices change.",
    });
  }

  const sections = [
    ["planType", "Plan types"],
    ["intervals", "Billing periods"],
    ["plans", "Plans & prices"],
    ["features", "Feature table"],
  ]
    .map(([k, label]) => ({ key: k, label, d: delta(log, k) }))
    .filter((s) => s.d);

  const st = STATUS[row.status] || { label: row.status, cls: "muted" };
  const open = row.status === "reviewing";

  return (
    <div className={`${styles.root} ${ui.root}`}>
      <Crumbs
        here={row.sku_name}
        onBack={() => navigate("/dashboard/productapproval")}
      />

      <div className={`${styles.head} ${ui.head}`}>
        <div className={ui.identity}>
          <Tile name={row.sku_name} src={row.logo} />
          <div>
            <h1 className={`${styles.h1} ${ui.h1}`}>{row.sku_name}</h1>
            <p className={`${styles.sub} ${ui.sub}`}>
              <span>{row.vendor_name}</span>
              <span className={ui.subDot}>·</span>
              <span>{CHANGE_TYPE[row.change_type] || "—"}</span>
              <span className={ui.subDot}>·</span>
              <span>submitted {ago(row.submitted_at)}</span>
              <Badge kind={st.cls}>{st.label}</Badge>
            </p>
          </div>
        </div>
        {/* Green / amber / red, in the order a reviewer decides: publish it,
            send it back, or end it. Approve is the only solid fill on the
            screen; Reject stays a tint until the pointer commits to it. */}
        {open && (
          <div className={`${styles.actions} ${ui.actions}`}>
            <button
              type="button"
              disabled={busy}
              className={`${ui.act} ${ui.actGo}`}
              onClick={() => {
                // Used to only switch tabs — a no-op when already on pricing.
                // Switch AND ask the panel to open the approve confirmation.
                setTab("pricing");
                setApproveRequest((n) => n + 1);
              }}
              title="Opens the approval confirmation (with the existing-customer options)"
            >
              Approve…
            </button>
            <button
              type="button"
              disabled={busy}
              className={`${ui.act} ${ui.actHold}`}
              onClick={() => setAsking("changes")}
            >
              Request changes
            </button>
            <button
              type="button"
              disabled={busy}
              className={`${ui.act} ${ui.actStop}`}
              onClick={() => setAsking("reject")}
            >
              Reject
            </button>
          </div>
        )}
      </div>

      {row.decision_note && (
        <div className={`${styles.card} ${ui.card} ${ui.callout} ${styles.noteBox} ${ui.noteBox}`}>
          <b>Reviewer note</b>
          <p>{row.decision_note}</p>
        </div>
      )}

      {/* Sending it back is amber, ending it is red — the panel takes the
          colour of the decision it is collecting, so the reviewer can see
          which of the two they are in. */}
      {asking && (
        <div
          className={`${styles.card} ${ui.card} ${ui.callout} ${styles.askBox} ${ui.askBox} ${
            asking === "changes" ? ui.askChanges : ""
          }`}
        >
          <b>
            {asking === "changes"
              ? "What does the vendor need to fix?"
              : "Why are you rejecting this?"}
          </b>
          <p className={styles.sub}>
            {asking === "changes"
              ? "They see this note, keep their work, and resubmit."
              : "Terminal. The vendor has to start the submission again."}
          </p>
          <textarea
            className={`${styles.textarea} ${ui.textarea}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              asking === "changes"
                ? "e.g. Plan descriptions are placeholder text — please rewrite before resubmitting."
                : "e.g. Product duplicates an existing listing."
            }
          />
          <div className={`${styles.actions} ${ui.actions}`}>
            <button
              type="button"
              disabled={note.trim().length < 5 || busy}
              className={`${ui.act} ${
                asking === "changes" ? ui.actHold : ui.actStop
              }`}
              onClick={() => decide(asking)}
            >
              {asking === "changes" ? "Send back to vendor" : "Reject submission"}
            </button>
            <button
              type="button"
              className={`${ui.act} ${ui.actQuiet}`}
              onClick={() => {
                setAsking(null);
                setNote("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {attention.length > 0 && (
        <div className={`${styles.card} ${ui.card} ${ui.callout} ${styles.attention} ${ui.attention}`}>
          <b>Needs your attention</b>
          <ul>
            {attention.map((a, i) => (
              <li key={i} className={a.severity === "block" ? styles.block : ""}>
                {a.text}
                {a.action && (
                  <button
                    type="button"
                    className={styles.link}
                    onClick={() =>
                      a.action.tab ? setTab(a.action.tab) : navigate(a.action.to)
                    }
                  >
                    {a.action.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={`${styles.tabs} ${ui.tabs}`} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "summary"}
          className={
            tab === "summary"
              ? `${styles.tabOn} ${ui.tabOn}`
              : `${styles.tab} ${ui.tab}`
          }
          onClick={() => setTab("summary")}
        >
          What changed
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "listing"}
          className={
            tab === "listing"
              ? `${styles.tabOn} ${ui.tabOn}`
              : `${styles.tab} ${ui.tab}`
          }
          onClick={() => setTab("listing")}
        >
          Listing
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pricing"}
          className={
            tab === "pricing"
              ? `${styles.tabOn} ${ui.tabOn}`
              : `${styles.tab} ${ui.tab}`
          }
          onClick={() => setTab("pricing")}
        >
          Full pricing &amp; publish
        </button>
        {/* Lifted out of the pricing panel. It was a second tab strip nested
            inside this one, which read as a bug. */}
        <button
          type="button"
          role="tab"
          aria-selected={tab === "chat"}
          className={
            tab === "chat"
              ? `${styles.tabOn} ${ui.tabOn}`
              : `${styles.tab} ${ui.tab}`
          }
          onClick={() => setTab("chat")}
        >
          Vendor chat
        </button>
      </div>

      {tab === "summary" && (
        <>
          <div className={`${styles.card} ${ui.card}`}>
            <h3>What changed</h3>
            {sections.length === 0 ? (
              <p className={styles.note}>
                No structural diff recorded — this submission predates change
                tracking, or nothing in the pricing structure moved.
              </p>
            ) : (
              <table className={`${styles.table} ${ui.table}`}>
                <thead>
                  <tr>
                    <th>Section</th>
                    <th className={`${styles.num} ${ui.numCol}`}>Before</th>
                    <th className={`${styles.num} ${ui.numCol}`}>After</th>
                    <th className={`${styles.num} ${ui.numCol}`}>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((s) => (
                    <tr key={s.key}>
                      <td className={ui.cellStrong}>{s.label}</td>
                      <td className={styles.num}>{s.d.before}</td>
                      <td className={styles.num}>{s.d.after}</td>
                      <td className={styles.num}>
                        <span
                          className={
                            s.d.net > 0
                              ? styles.pos
                              : s.d.net < 0
                                ? styles.neg
                                : ""
                          }
                        >
                          {s.d.net > 0 ? `+${s.d.net}` : s.d.net}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* The cap arithmetic, done for the reviewer rather than by them. */}
          <div className={`${styles.card} ${ui.card}`}>
            <h3>
              Price cap{" "}
              {cap.over_cap ? (
                <Badge kind="danger">breached</Badge>
              ) : (
                <Badge kind="ok">within ±25%/year</Badge>
              )}
            </h3>
            {findings.length === 0 ? (
              <p className={styles.note}>
                No price moves beyond the cap. The cap is net against the price
                twelve months earlier, not cumulative.
              </p>
            ) : (
              <table className={`${styles.table} ${ui.table}`}>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th className={`${styles.num} ${ui.numCol}`}>Was</th>
                    <th className={`${styles.num} ${ui.numCol}`}>Now</th>
                    <th className={`${styles.num} ${ui.numCol}`}>Change</th>
                    <th className={`${styles.num} ${ui.numCol}`}>Limit</th>
                    <th>Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((f, i) => (
                    <tr key={i}>
                      <td className={ui.cellStrong}>{f.plan_name}</td>
                      <td className={styles.num}>{f.baseline}</td>
                      <td className={styles.num}>{f.proposed}</td>
                      <td className={`${styles.num} ${styles.neg}`}>
                        {typeof f.change === "number"
                          ? `${(f.change * 100).toFixed(1)}%`
                          : f.change}
                      </td>
                      <td className={styles.num}>
                        {typeof f.limit === "number"
                          ? `${(f.limit * 100).toFixed(0)}%`
                          : f.limit}
                      </td>
                      <td className={ui.cellQuiet}>{f.basis || f.summary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "listing" && (
        <ListingReview
          product={detail?.product}
          planType={detail?.planType || []}
          planCount={(detail?.plans || []).length}
          intervalCount={(detail?.intervals || []).length}
          featureCount={(detail?.features || []).length}
          contentBefore={detail?.contentBefore || null}
          contentAtSubmit={detail?.contentAtSubmit || null}
          pendingProduct={detail?.pendingProduct || null}
          hasPendingContent={Boolean(detail?.hasPendingContent)}
          onOpenPricing={() => setTab("pricing")}
        />
      )}

      {/* Rebuilt. The T1/T2/T3 pricing cards showed one billing interval at a
          time and never computed the carve-out; this renders the whole
          plan x period matrix with the margin, the diff and the cap in one
          place. The publish path — including the existing-customer propagation
          options — is preserved unchanged inside the panel.

          Both tabs mount the SAME panel, switched by `view`, so moving to the
          chat and back does not refetch the submission or throw away an
          in-progress price edit. */}
      {(tab === "pricing" || tab === "chat") && (
        <PricingReviewPanel
          view={tab}
          approveRequest={approveRequest}
          selectedMarketPlace={deep || legacyRow}
          setSelectedMarketPlace={(v) => {
            // The panel "closes" with {} after approve (legacy contract). {} is
            // truthy, so it used to become the selection, the panel refetched
            // with approval_id undefined and the server 500'd right after a
            // successful approval. Empty = back to the queue.
            if (!v || !Object.keys(v).length) navigate("/dashboard/productapproval");
            else setDeep(v);
          }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

export default function ProductApprovals() {
  const { approvalId } = useParams();
  return approvalId ? <Review approvalId={approvalId} /> : <Queue />;
}
